// ══════════════════════════════════════════════════════════════
// COMPASS – Country Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CountryActions } from './country.actions';
import { selectCountriesLoaded } from './country.selectors';
import { ApiConfiguration } from '../../api/api-configuration';
import { getAll6 } from '../../api/fn/country/get-all-6';
import { CountryDto } from '../../api/models/country-dto';
import { setBackendCountryNames } from '../../../shared/constants/country-codes';
import { errorMessage, readApiError } from '../../utils/api-error.utils';

@Injectable()
export class CountryEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly store = inject(Store);

  /** Load countries from API (skipped if already loaded); a reload always fetches. */
  readonly loadCountries$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(CountryActions.loadCountries),
        withLatestFrom(this.store.select(selectCountriesLoaded)),
        filter(([, loaded]) => !loaded),
      ),
      this.actions$.pipe(ofType(CountryActions.reloadCountries)),
    ).pipe(
      switchMap(() =>
        getAll6(this.http, this.apiConfig.rootUrl).pipe(
          map((response) => {
            const countries: CountryDto[] = (response.body as unknown as CountryDto[]) ?? [];
            return CountryActions.loadCountriesSuccess({ countries });
          }),
          catchError((error) =>
            [CountryActions.loadCountriesFailure({ error: errorMessage(readApiError(error), 'ERROR.LOAD_COUNTRIES_FAILED') })]
          )
        )
      )
    )
  );

  /** Read-only displays resolve country names through getCountryName(), which prefers these names. */
  readonly syncCountryNames$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CountryActions.loadCountriesSuccess),
        tap(({ countries }) => setBackendCountryNames(countries)),
      ),
    { dispatch: false },
  );
}
